import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Image as ImageIcon, Plus, RefreshCw, Trash2,
  PenSquare, Upload, X, Check, Copy,
  MapPin, Sparkles, ExternalLink, Globe, Camera
} from "lucide-react";
import { toast } from "sonner";

const GALLERY_CATEGORIES = ["Classroom", "Live Sessions", "Student Moments", "Events"];
const SITE_MEDIA_CATEGORY = "Site Media";
const ALL_CATEGORIES = [SITE_MEDIA_CATEGORY, ...GALLERY_CATEGORIES];

const CAT_COLORS = {
  "Site Media":      "bg-zinc-500/10 text-zinc-400 border border-zinc-700/40",
  "Classroom":       "bg-blue-500/15 text-blue-400 border border-blue-500/30",
  "Live Sessions":   "bg-purple-500/15 text-purple-400 border border-purple-500/30",
  "Student Moments": "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30",
  "Events":          "bg-amber-500/15 text-amber-400 border border-amber-500/30",
};

const EMPTY_FORM = { url: "", category: "Classroom", location: "", alt_text: "", caption: "" };

const MediaManager = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [displayCount, setDisplayCount] = useState(32);
  const [searchTerm, setSearchTerm] = useState("");

  // Top Section Mode: "site_media" (Website Images) or "gallery" (Public Gallery Landing Page)
  const [activeSection, setActiveSection] = useState("gallery");
  // Sub-filter for Gallery mode
  const [galleryFilter, setGalleryFilter] = useState("All");

  // Add / Upload modal form
  const [form, setForm] = useState(EMPTY_FORM);
  const [showAddModal, setShowAddModal] = useState(false);

  // Edit modal
  const [editImg, setEditImg] = useState(null);
  const [editData, setEditData] = useState({ category: "", location: "", alt_text: "", caption: "" });
  const [saving, setSaving] = useState(false);

  // Replace modal
  const [replacingImg, setReplacingImg] = useState(null);
  const [replaceUrl, setReplaceUrl] = useState("");
  const [replaceAlt, setReplaceAlt] = useState("");
  const [replaceUploading, setReplaceUploading] = useState(false);

  useEffect(() => {
    fetchGallery();
  }, []);

  useEffect(() => {
    setDisplayCount(32);
  }, [activeSection, galleryFilter, searchTerm]);

  const fetchGallery = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/gallery?scope=all`, { withCredentials: true });
      setImages(res.data || []);
    } catch {
      toast.error("Failed to fetch media library");
    } finally {
      setLoading(false);
    }
  };

  const syncMedia = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/gallery/seed`, {}, { withCredentials: true });
      toast.success("All website images synchronized with page locations!");
      await fetchGallery();
    } catch {
      toast.error("Failed to sync website media");
    } finally {
      setLoading(false);
    }
  };

  // Upload file to GridFS
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "media");
    try {
      const res = await axios.post(`${API}/uploads`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fileUrl = res.data?.url || res.data?.file_url;
      setForm(f => ({
        ...f,
        url: fileUrl,
        category: activeSection === "gallery" ? (GALLERY_CATEGORIES.includes(f.category) ? f.category : "Classroom") : SITE_MEDIA_CATEGORY,
        location: activeSection === "gallery" ? (f.location || "Public Gallery Landing Page") : f.location,
      }));
      toast.success("File uploaded! Please confirm category and caption.");
      setShowAddModal(true);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const addImage = async () => {
    if (!form.url) return toast.error("Image URL or uploaded file is required");
    const targetCategory = activeSection === "gallery"
      ? (GALLERY_CATEGORIES.includes(form.category) ? form.category : "Classroom")
      : SITE_MEDIA_CATEGORY;

    try {
      const payload = {
        url: form.url,
        category: targetCategory,
        location: form.location || (activeSection === "gallery" ? `Public Gallery - ${targetCategory}` : "Custom Website Media"),
        alt_text: form.alt_text || form.caption || "Jamadhan Trading Academy",
        caption: form.caption || "",
      };

      const res = await axios.post(`${API}/gallery`, payload, { withCredentials: true });
      setImages(prev => [res.data, ...prev]);
      setShowAddModal(false);
      setForm(EMPTY_FORM);
      if (activeSection === "gallery") {
        toast.success("✅ Photo added to Public Gallery! It is now live on /gallery for students.");
      } else {
        toast.success("✅ Website image added to media library.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add image");
    }
  };

  const deleteImage = async (id) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;
    try {
      await axios.delete(`${API}/gallery/${id}`, { withCredentials: true });
      setImages(prev => prev.filter(i => i.image_id !== id));
      toast.success("Image removed successfully");
    } catch {
      toast.error("Failed to delete image");
    }
  };

  // Replace handlers
  const openReplace = (img) => {
    setReplacingImg(img);
    setReplaceUrl(img.url);
    setReplaceAlt(img.alt_text || "");
  };

  const handleReplaceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !replacingImg) return;
    setReplaceUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "media");
    try {
      const res = await axios.post(`${API}/uploads`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fileUrl = res.data?.url || res.data?.file_url;
      setReplaceUrl(fileUrl);
      toast.success("Replacement file uploaded! Click Confirm Replace to apply.");
    } catch {
      toast.error("Replacement upload failed");
    } finally {
      setReplaceUploading(false);
      e.target.value = "";
    }
  };

  const executeReplace = async () => {
    if (!replacingImg || !replaceUrl) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/gallery/${replacingImg.image_id}`, {
        url: replaceUrl,
        alt_text: replaceAlt,
        location: replacingImg.location || (replacingImg.category === SITE_MEDIA_CATEGORY ? "Site Media Asset" : `Public Gallery - ${replacingImg.category}`),
      }, { withCredentials: true });

      setImages(prev => prev.map(i =>
        i.image_id === replacingImg.image_id ? { ...i, url: replaceUrl, alt_text: replaceAlt } : i
      ));
      if (replacingImg.category === SITE_MEDIA_CATEGORY) {
        toast.success("Image replaced site-wide! All website pages and courses updated.");
      } else {
        toast.success("Gallery image replaced! Live on /gallery.");
      }
      setReplacingImg(null);
      fetchGallery();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to replace image");
    } finally {
      setSaving(false);
    }
  };

  // Edit handlers
  const openEdit = (img) => {
    setEditImg(img);
    setEditData({
      location: img.location || (img.category === SITE_MEDIA_CATEGORY ? "Site Media" : `Public Gallery - ${img.category}`),
      category: img.category || (activeSection === "gallery" ? "Classroom" : SITE_MEDIA_CATEGORY),
      alt_text: img.alt_text || "",
      caption:  img.caption || "",
      url:      img.url || "",
    });
  };

  const saveEdit = async () => {
    if (!editImg) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/gallery/${editImg.image_id}`, editData, { withCredentials: true });
      setImages(prev => prev.map(i =>
        i.image_id === editImg.image_id ? { ...i, ...editData } : i
      ));
      setEditImg(null);
      toast.success("Image details updated successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("URL copied to clipboard");
  };

  // Deduplicate by URL
  const uniqueImages = Array.from(new Map(images.map(img => [img.url, img])).values());

  // Segregate by mode: Website Images vs Gallery Images
  const siteMediaImages = uniqueImages.filter(img => img.category === SITE_MEDIA_CATEGORY);
  const galleryImages = uniqueImages.filter(img => img.category !== SITE_MEDIA_CATEGORY);

  // Active pool based on selected mode
  const currentPool = activeSection === "gallery" ? galleryImages : siteMediaImages;

  // Search filter
  const searchedImages = currentPool.filter(i => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (i.alt_text && i.alt_text.toLowerCase().includes(term)) ||
           (i.caption && i.caption.toLowerCase().includes(term)) ||
           (i.url && i.url.toLowerCase().includes(term)) ||
           (i.location && i.location.toLowerCase().includes(term)) ||
           (i.category && i.category.toLowerCase().includes(term));
  });

  // Sub-filter for Gallery mode
  const filtered = activeSection === "gallery"
    ? (galleryFilter === "All" ? searchedImages : searchedImages.filter(i => i.category === galleryFilter))
    : searchedImages;

  const displayedImages = filtered.slice(0, displayCount);

  return (
    <div className="space-y-6 pb-20">

      {/* ═══ TOP HEADER & MODE SELECTOR ═══ */}
      <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.08] shadow-sm">
        <CardHeader className="p-5 pb-4 space-y-4">
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                  <ImageIcon className="w-5 h-5" />
                </div>
                <div>
                  <CardTitle className="text-gray-900 dark:text-white font-outfit text-xl flex items-center gap-2">
                    Media & Gallery Manager
                    <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 text-xs">
                      Admin & Tech
                    </Badge>
                  </CardTitle>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                    Manage website UI media and student public gallery photos with 1-click replacement.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchGallery}
                className="text-gray-600 dark:text-zinc-300 hover:text-black dark:hover:text-white rounded-xl h-9"
              >
                <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
              </Button>

              {activeSection === "site_media" ? (
                <Button
                  size="sm"
                  onClick={syncMedia}
                  className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:hover:bg-emerald-900/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 rounded-xl font-semibold h-9 shadow-xs"
                  title="Populate and refresh all core site images with location details"
                >
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Sync Website Images
                </Button>
              ) : (
                <a
                  href="/gallery"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-zinc-300 hover:text-white transition-colors h-9"
                >
                  <ExternalLink className="w-3.5 h-3.5 text-emerald-400" />
                  View Public Gallery (/gallery)
                </a>
              )}

              {/* Upload Button */}
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  className="absolute inset-0 opacity-0 cursor-pointer w-full h-full z-10"
                  onChange={handleFileUpload}
                  disabled={uploading}
                />
                <Button
                  disabled={uploading}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl h-9 px-4 shadow-sm"
                >
                  {uploading ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                  {activeSection === "gallery" ? "Upload Gallery Photo" : "Upload Website Image"}
                </Button>
              </div>

              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setForm({
                    ...EMPTY_FORM,
                    category: activeSection === "gallery" ? "Classroom" : SITE_MEDIA_CATEGORY,
                    location: activeSection === "gallery" ? "Public Gallery Landing Page" : ""
                  });
                  setShowAddModal(true);
                }}
                className="border-slate-300 dark:border-white/15 text-slate-700 dark:text-slate-200 font-semibold rounded-xl h-9"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Add by URL
              </Button>
            </div>
          </div>

          {/* ═══ TWO PRIMARY MODE TABS ═══ */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-gray-200 dark:border-white/[0.08]">
            {/* TAB 1: GALLERY IMAGES */}
            <button
              type="button"
              onClick={() => {
                setActiveSection("gallery");
                setSearchTerm("");
              }}
              className={`p-3.5 rounded-2xl border text-left transition-all flex items-start gap-3 ${
                activeSection === "gallery"
                  ? "bg-emerald-50/70 dark:bg-emerald-950/30 border-emerald-500/60 shadow-sm ring-1 ring-emerald-500/40"
                  : "bg-slate-50 dark:bg-white/[0.02] border-slate-200 dark:border-white/[0.06] hover:border-slate-300 dark:hover:border-white/15 opacity-75 hover:opacity-100"
              }`}
            >
              <div className={`p-2.5 rounded-xl shrink-0 ${
                activeSection === "gallery"
                  ? "bg-emerald-500 text-black shadow-xs font-bold"
                  : "bg-slate-200 dark:bg-white/10 text-slate-700 dark:text-zinc-300"
              }`}>
                <Camera className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-outfit font-bold text-sm text-gray-900 dark:text-white flex items-center gap-1.5">
                    📸 Gallery Images
                    <span className="text-[10px] uppercase font-mono px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 font-bold">
                      Public Page
                    </span>
                  </p>
                  <Badge className="bg-emerald-600/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 text-xs">
                    {galleryImages.length} photos
                  </Badge>
                </div>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 line-clamp-2">
                  Photos shown on the public <strong className="text-emerald-600 dark:text-emerald-400">/gallery</strong> landing page for students & visitors (Classroom, Live Sessions, Student Moments, Events).
                </p>
              </div>
            </button>

            {/* TAB 2: WEBSITE IMAGES (SITE MEDIA) */}
            <button
              type="button"
              onClick={() => {
                setActiveSection("site_media");
                setSearchTerm("");
              }}
              className={`p-3.5 rounded-2xl border text-left transition-all flex items-start gap-3 ${
                activeSection === "site_media"
                  ? "bg-cyan-50/70 dark:bg-cyan-950/30 border-cyan-500/60 shadow-sm ring-1 ring-cyan-500/40"
                  : "bg-slate-50 dark:bg-white/[0.02] border-slate-200 dark:border-white/[0.06] hover:border-slate-300 dark:hover:border-white/15 opacity-75 hover:opacity-100"
              }`}
            >
              <div className={`p-2.5 rounded-xl shrink-0 ${
                activeSection === "site_media"
                  ? "bg-cyan-500 text-black shadow-xs font-bold"
                  : "bg-slate-200 dark:bg-white/10 text-slate-700 dark:text-zinc-300"
              }`}>
                <Globe className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-outfit font-bold text-sm text-gray-900 dark:text-white flex items-center gap-1.5">
                    🌐 Website Images (Site Media)
                    <span className="text-[10px] uppercase font-mono px-1.5 py-0.2 rounded bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 font-bold">
                      Site-Wide
                    </span>
                  </p>
                  <Badge className="bg-cyan-600/15 text-cyan-600 dark:text-cyan-400 border border-cyan-500/30 text-xs">
                    {siteMediaImages.length} assets
                  </Badge>
                </div>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 line-clamp-2">
                  Branding Logo, Hero Banners, Section Graphics, and Course Thumbnails. Replace any image here to instantly update it site-wide.
                </p>
              </div>
            </button>
          </div>

          {/* ═══ FILTER & SEARCH BAR ═══ */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
            <div className="relative w-full sm:w-80">
              <input
                type="text"
                placeholder={activeSection === "gallery" ? "Search gallery by caption, category, alt text..." : "Search by page location, alt text, URL..."}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full text-xs px-3 py-2 pl-8 rounded-xl bg-slate-100 dark:bg-white/[0.04] border border-slate-200 dark:border-white/[0.08] text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-hidden focus:border-emerald-500"
              />
              <ImageIcon className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            </div>

            {/* Gallery Category Pills (Only when in Gallery mode) */}
            {activeSection === "gallery" && (
              <div className="flex flex-wrap gap-1.5 w-full sm:w-auto">
                {["All", ...GALLERY_CATEGORIES].map(cat => {
                  const count = cat === "All"
                    ? galleryImages.length
                    : galleryImages.filter(i => i.category === cat).length;
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setGalleryFilter(cat)}
                      className={`px-3 py-1 text-xs font-semibold rounded-lg border transition-all ${
                        galleryFilter === cat
                          ? "bg-emerald-600 text-white border-emerald-600 shadow-xs font-bold"
                          : "bg-slate-100 dark:bg-white/[0.04] border-slate-200 dark:border-white/[0.08] text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                      }`}
                    >
                      {cat} <span className="opacity-70 text-[11px]">({count})</span>
                    </button>
                  );
                })}
              </div>
            )}

            {activeSection === "site_media" && (
              <div className="text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                <span>Showing website UI assets tagged with page locations.</span>
              </div>
            )}
          </div>
        </CardHeader>

        {/* ═══ IMAGES GRID ═══ */}
        <CardContent className="pt-2">
          {loading ? (
            <div className="py-16 text-center">
              <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-xs text-zinc-400 mt-3 font-medium">Loading {activeSection === "gallery" ? "gallery photos" : "website images"}...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-gray-400 dark:text-[#52525B]">
              <ImageIcon className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-semibold text-gray-700 dark:text-zinc-300">
                {searchTerm ? "No images match your search." : activeSection === "gallery" ? "No gallery photos found." : "No website images cataloged."}
              </p>
              <p className="text-xs mt-1 text-zinc-500">
                {activeSection === "gallery"
                  ? "Click 'Upload Gallery Photo' above to add photos to the public gallery."
                  : "Click 'Sync Website Images' to catalog all site media."}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {displayedImages.map(img => (
                  <div
                    key={img.image_id || img.url}
                    className="relative group rounded-2xl overflow-hidden border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-[#0B1120] shadow-xs flex flex-col justify-between hover:shadow-md transition-all"
                  >
                    <div className="relative">
                      {/* Top Badge: Location or Category */}
                      <div className="absolute top-2 left-2 right-2 flex items-center justify-between gap-1 z-10 pointer-events-none">
                        <span className="bg-slate-900/90 backdrop-blur-md text-white text-[10px] font-bold px-2 py-0.5 rounded-md flex items-center gap-1 max-w-[95%] truncate border border-white/10 shadow-xs">
                          {activeSection === "gallery" ? (
                            <span className="text-emerald-400 font-bold uppercase">{img.category}</span>
                          ) : (
                            <>
                              <MapPin className="w-2.5 h-2.5 text-cyan-400 shrink-0" />
                              <span className="truncate">{img.location || "Site Media"}</span>
                            </>
                          )}
                        </span>
                      </div>

                      <div className="w-full aspect-[4/3] bg-black/40 overflow-hidden">
                        <img
                          src={img.url}
                          alt={img.alt_text || img.caption || img.category}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          onError={e => { e.target.style.display = "none"; }}
                          loading="lazy"
                          decoding="async"
                        />
                      </div>

                      {/* Hover Overlay with Action Buttons */}
                      <div className="absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2.5 z-20">
                        <div className="flex justify-between w-full items-center">
                          <button
                            type="button"
                            onClick={() => copyUrl(img.url)}
                            className="h-7 px-2 rounded-lg bg-white/15 hover:bg-white/25 text-white text-[10px] font-semibold flex items-center gap-1 transition-colors"
                            title="Copy URL"
                          >
                            <Copy className="w-3 h-3" /> Copy URL
                          </button>
                          
                          <div className="flex gap-1.5">
                            <button
                              type="button"
                              onClick={() => openReplace(img)}
                              className="w-7 h-7 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center transition-colors shadow-xs"
                              title="Replace this image"
                            >
                              <RefreshCw className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => openEdit(img)}
                              className="w-7 h-7 rounded-full bg-white/20 hover:bg-emerald-500/40 text-white hover:text-emerald-300 flex items-center justify-center transition-colors"
                              title="Edit Details"
                            >
                              <PenSquare className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteImage(img.image_id)}
                              className="w-7 h-7 rounded-full bg-white/20 hover:bg-red-500/50 text-red-300 flex items-center justify-center transition-colors"
                              title="Delete image"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <button
                            type="button"
                            onClick={() => openReplace(img)}
                            className="w-full py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black flex items-center justify-center gap-1.5 transition-colors shadow-sm"
                          >
                            <RefreshCw className="w-3 h-3" /> Replace Image
                          </button>
                          {img.alt_text ? (
                            <p className="text-[10px] text-white/95 truncate bg-black/70 rounded px-1.5 py-0.5 border border-white/10">
                              <span className="text-emerald-400 font-bold">Alt:</span> {img.alt_text}
                            </p>
                          ) : (
                            <p className="text-[10px] text-amber-300 truncate bg-amber-950/80 border border-amber-800/80 rounded px-1.5 py-0.5">
                              ⚠️ Missing Alt Text
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Card Footer */}
                    <div className="p-3 bg-white dark:bg-[#080D1A] border-t border-slate-100 dark:border-white/[0.06] space-y-1.5">
                      <div className="flex items-center justify-between gap-1">
                        <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${CAT_COLORS[img.category] || "text-zinc-500 bg-gray-100 dark:bg-white/5"}`}>
                          {img.category}
                        </span>
                        <button
                          type="button"
                          onClick={() => openReplace(img)}
                          className="text-[11px] text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1 font-bold"
                        >
                          <RefreshCw className="w-3 h-3" /> Replace
                        </button>
                      </div>

                      <div className="space-y-0.5">
                        {activeSection === "gallery" ? (
                          <>
                            <p className="text-xs font-bold text-gray-900 dark:text-white truncate" title={img.caption || img.alt_text}>
                              {img.caption || img.alt_text || "Gallery Photo"}
                            </p>
                            <p className="text-[10px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1 font-medium">
                              ✓ Live on /gallery
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-[11px] font-bold text-gray-900 dark:text-white flex items-center gap-1 truncate" title={img.location}>
                              <MapPin className="w-3 h-3 text-cyan-400 shrink-0" />
                              <span className="truncate">{img.location || "Site Media"}</span>
                            </p>
                            <p className="text-[11px] text-gray-600 dark:text-gray-300 truncate" title={img.alt_text || "No Alt text"}>
                              {img.alt_text ? (
                                <span><strong className="text-emerald-600 dark:text-emerald-400 font-semibold">Alt:</strong> {img.alt_text}</span>
                              ) : (
                                <span className="text-amber-600 dark:text-amber-400 italic">⚠️ Missing Alt text</span>
                              )}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {displayCount < filtered.length && (
                <div className="flex justify-center pt-4">
                  <Button
                    variant="outline"
                    className="border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white rounded-xl text-xs font-bold"
                    onClick={() => setDisplayCount(prev => prev + 32)}
                  >
                    Load More Images ({filtered.length - displayCount} remaining)
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ═══ ADD / UPLOAD MODAL ═══ */}
      <Dialog open={showAddModal} onOpenChange={open => !open && setShowAddModal(false)}>
        <DialogContent className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white font-outfit flex items-center gap-2">
              <Plus className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              {activeSection === "gallery" ? "Add Photo to Public Gallery (/gallery)" : "Add Website Image Asset"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1">
              <Label className="text-gray-700 dark:text-gray-300 text-xs font-semibold">Image URL</Label>
              <Input
                value={form.url}
                onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
                placeholder="https://... or uploaded image URL"
                className="bg-white dark:bg-[#07090E] border-gray-300 dark:border-white/[0.12] text-gray-900 dark:text-white text-sm"
              />
            </div>

            {form.url && (
              <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-white/10 w-full h-36 bg-black/40">
                <img src={form.url} alt="preview" className="w-full h-full object-cover" />
              </div>
            )}

            {/* Category */}
            <div className="space-y-1">
              <Label className="text-gray-700 dark:text-gray-300 text-xs font-semibold">
                {activeSection === "gallery" ? "Gallery Category (Shown on /gallery)" : "Category"}
              </Label>
              <Select
                value={form.category}
                onValueChange={v => setForm(f => ({ ...f, category: v }))}
              >
                <SelectTrigger className="bg-white dark:bg-[#07090E] border-gray-300 dark:border-white/[0.12] text-gray-900 dark:text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white">
                  {activeSection === "gallery" ? (
                    GALLERY_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)
                  ) : (
                    ALL_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Caption */}
            <div className="space-y-1">
              <Label className="text-gray-700 dark:text-gray-300 text-xs font-semibold">
                {activeSection === "gallery" ? "Caption / Title (Shown on Gallery Card)" : "Caption (Optional)"}
              </Label>
              <Input
                value={form.caption}
                onChange={e => setForm(f => ({ ...f, caption: e.target.value }))}
                placeholder="e.g. Offline Classroom Chart Breakdown Session"
                className="bg-white dark:bg-[#07090E] border-gray-300 dark:border-white/[0.12] text-gray-900 dark:text-white text-sm"
              />
            </div>

            {/* Location */}
            {activeSection === "site_media" && (
              <div className="space-y-1">
                <Label className="text-gray-700 dark:text-gray-300 text-xs font-semibold">Page Location / Where Used</Label>
                <Input
                  value={form.location || ""}
                  onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                  placeholder="e.g. Homepage - Hero Banner, About - Reception"
                  className="bg-white dark:bg-[#07090E] border-gray-300 dark:border-white/[0.12] text-gray-900 dark:text-white text-sm"
                />
              </div>
            )}

            {/* Alt text */}
            <div className="space-y-1">
              <Label className="text-gray-700 dark:text-gray-300 text-xs font-semibold flex items-center gap-1.5">
                Alt Text (SEO Description)
                <span className="text-emerald-700 dark:text-emerald-300 text-[10px] font-mono font-medium tracking-wider bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 px-1 py-0.2 rounded">
                  SEO
                </span>
              </Label>
              <Input
                value={form.alt_text}
                onChange={e => setForm(f => ({ ...f, alt_text: e.target.value }))}
                placeholder="e.g. Students analyzing live stock charts at Jamadhan"
                className="bg-white dark:bg-[#07090E] border-gray-300 dark:border-white/[0.12] text-gray-900 dark:text-white text-sm"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 mt-2">
            <Button variant="ghost" onClick={() => setShowAddModal(false)} className="text-gray-500 dark:text-[#A1A1AA]">
              Cancel
            </Button>
            <Button onClick={addImage} className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl h-10 px-5 shadow-sm">
              <Plus className="w-4 h-4 mr-1.5" />
              {activeSection === "gallery" ? "Add to Public Gallery" : "Add Website Image"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ EDIT IMAGE DETAILS MODAL ═══ */}
      <Dialog open={!!editImg} onOpenChange={open => !open && setEditImg(null)}>
        <DialogContent className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white font-outfit flex items-center gap-2">
              <PenSquare className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              Edit Image Details
            </DialogTitle>
          </DialogHeader>

          {editImg && (
            <div className="space-y-4">
              <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-white/10 h-40 w-full bg-black/40">
                <img src={editImg.url} alt="" className="w-full h-full object-cover" />
              </div>

              <div className="space-y-1.5">
                <Label className="text-gray-700 dark:text-gray-300 text-xs font-semibold">Category</Label>
                <Select value={editData.category} onValueChange={v => setEditData(d => ({ ...d, category: v }))}>
                  <SelectTrigger className="bg-white dark:bg-[#07090E] border-gray-300 dark:border-white/[0.12] text-gray-900 dark:text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white">
                    {ALL_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-gray-700 dark:text-gray-300 text-xs font-semibold">Caption</Label>
                <Input
                  value={editData.caption || ""}
                  onChange={e => setEditData(d => ({ ...d, caption: e.target.value }))}
                  placeholder="Short description..."
                  className="bg-white dark:bg-[#07090E] border-gray-300 dark:border-white/[0.12] text-gray-900 dark:text-white text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-gray-700 dark:text-gray-300 text-xs font-semibold flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-cyan-500" /> Page Location / Where Used
                </Label>
                <Input
                  value={editData.location || ""}
                  onChange={e => setEditData(d => ({ ...d, location: e.target.value }))}
                  placeholder="e.g. Global Header - Logo, Homepage - Hero"
                  className="bg-white dark:bg-[#07090E] border-gray-300 dark:border-white/[0.12] text-gray-900 dark:text-white text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-gray-700 dark:text-gray-300 text-xs font-semibold flex items-center gap-2">
                  Alt Text (SEO Description)
                  <span className="text-emerald-700 dark:text-emerald-300 text-[10px] font-mono font-medium tracking-wider bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 px-1 py-0.2 rounded">
                    SEO
                  </span>
                </Label>
                <Input
                  value={editData.alt_text}
                  onChange={e => setEditData(d => ({ ...d, alt_text: e.target.value }))}
                  placeholder="Describe image for search engines..."
                  className="bg-white dark:bg-[#07090E] border-gray-300 dark:border-white/[0.12] text-gray-900 dark:text-white text-sm"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 mt-2">
            <Button variant="ghost" onClick={() => setEditImg(null)} className="text-gray-500 dark:text-[#A1A1AA]">
              Cancel
            </Button>
            <Button onClick={saveEdit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl h-10 px-5 shadow-xs">
              {saving ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ REPLACE IMAGE MODAL ═══ */}
      <Dialog open={!!replacingImg} onOpenChange={open => !open && setReplacingImg(null)}>
        <DialogContent className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white font-outfit flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              {replacingImg?.category === SITE_MEDIA_CATEGORY ? "Replace Website Image" : "Replace Gallery Photo"}
            </DialogTitle>
          </DialogHeader>

          {replacingImg && (
            <div className="space-y-4">
              <div className="p-3.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-xs text-emerald-900 dark:text-emerald-200 flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold uppercase tracking-wider text-[10px] text-emerald-700 dark:text-emerald-400">Target Asset</p>
                  <p className="font-bold text-sm mt-0.5">{replacingImg.location || replacingImg.caption || replacingImg.category}</p>
                  <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-1">
                    {replacingImg.category === SITE_MEDIA_CATEGORY
                      ? "Replacing this image will automatically update it everywhere across the live website, courses, and search engines."
                      : "Replacing this image will update the photo on the public /gallery landing page."}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 items-center">
                <div>
                  <Label className="text-[11px] text-gray-500 mb-1 block font-semibold">Current Image</Label>
                  <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-white/10 h-28 w-full bg-black/30">
                    <img src={replacingImg.url} alt="Current" className="w-full h-full object-cover" />
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] text-gray-500 mb-1 block font-semibold">New Image Preview</Label>
                  <div className="rounded-xl overflow-hidden border border-emerald-500/40 h-28 w-full bg-black/30 flex items-center justify-center">
                    {replaceUrl ? (
                      <img src={replaceUrl} alt="New Preview" className="w-full h-full object-cover" />
                    ) : (
                      <p className="text-[11px] text-gray-400">Upload or enter URL</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Upload replacement file */}
              <div className="space-y-1.5">
                <Label className="text-gray-700 dark:text-gray-300 text-xs font-semibold">Upload Replacement File</Label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full z-10"
                    onChange={handleReplaceUpload}
                    disabled={replaceUploading}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={replaceUploading}
                    className="w-full justify-center bg-slate-100 dark:bg-white/[0.04] border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white text-xs h-10 font-bold"
                  >
                    {replaceUploading ? <RefreshCw className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-2 text-emerald-500" />}
                    Choose New File to Upload & Replace
                  </Button>
                </div>
              </div>

              {/* Or enter new URL */}
              <div className="space-y-1.5">
                <Label className="text-gray-700 dark:text-gray-300 text-xs font-semibold">Or New Image URL</Label>
                <Input
                  value={replaceUrl}
                  onChange={e => setReplaceUrl(e.target.value)}
                  placeholder="https://... or uploaded image URL"
                  className="bg-white dark:bg-[#07090E] border-gray-300 dark:border-white/[0.12] text-gray-900 dark:text-white text-xs"
                />
              </div>

              {/* Alt Text */}
              <div className="space-y-1.5">
                <Label className="text-gray-700 dark:text-gray-300 text-xs font-semibold flex items-center gap-1.5">
                  Alt Text (SEO Description)
                  <span className="text-emerald-700 dark:text-emerald-300 text-[10px] font-mono font-medium tracking-wider bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 px-1 py-0.2 rounded">
                    SEO
                  </span>
                </Label>
                <Input
                  value={replaceAlt}
                  onChange={e => setReplaceAlt(e.target.value)}
                  placeholder="Descriptive alt text for Google SEO..."
                  className="bg-white dark:bg-[#07090E] border-gray-300 dark:border-white/[0.12] text-gray-900 dark:text-white text-xs"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 mt-2">
            <Button variant="ghost" onClick={() => setReplacingImg(null)} className="text-gray-500 dark:text-[#A1A1AA]">
              Cancel
            </Button>
            <Button
              onClick={executeReplace}
              disabled={saving || !replaceUrl}
              className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl h-10 px-5 shadow-sm"
            >
              {saving ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
              Confirm Replace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default MediaManager;
